import { SelectInput } from 'playup-web-core';
import React from 'react';
import styled from 'styled-components';
import { selfExclude, takeBreak } from '../../../../api/users';

import appConfig from '../../../../appConfig';
import CheckBox from '../../../../components/CheckBox';
import Prompt from '../../../../components/Prompt';
import useAsyncAction from '../../../../hooks/useAsyncAction';
import useNotify from '../../../../hooks/useNotify';
import { parseApiError } from '../../../../utils/parseApiError';
import { closePeriodsUS } from '../../../constants/breaks';

const checkboxes = {
	IA: [
		'PlayUp’s self-exclusion list is independent from the Iowa Racing and Gaming Commission’s self-exclusion program: https://irgc.iowa.gov/self-exclusion-program',
		'I acknowledge the fact that casino companies might enact responsible gaming programs that are stricter than the self-exclusion program for gaming. By way of example, a casino may enact a more restrictive responsible gaming program in which persons signing up for self-exclusion are banned from all of their casino properties worldwide for the length of the self-exclusion terms. A casino ban may include its gaming, hotel and entertainment venues.',
		'I understand individual casinos have the right to change their responsible gaming programs to exclude me from their casinos even if I sign up only for the PlayUp Iowa self-exclusion list. I understand that the Iowa Racing and Gaming Commission is not responsible for keeping me informed of any changes.',
		'By initiating this online exclusion, I agree not to gamble at PlayUp in Iowa and understand I will no longer be able to perform any actions on the site, nor receive any marketing emails or promotion. I understand that all pending wagers will stand as placed and any winnings can be withdrawn by contacting customer support.',
	],
	OH: [
		"PlayUp's self-exclusion list is independent from the Ohio Casino Control Commission's voluntary exclusion program (VEP).",
		'I acknowledge the fact that casino companies might enact responsible gaming programs that are stricter than the self-exclusion program for gaming. By way of example, a casino may enact a more restrictive responsible gaming program in which persons signing up for self-exclusion are banned from all of their casinc properties worldwide for the length of the self-exclusion terms. A casino ban may include its gaming, hotel and entertainment venues',
		'I understand individual casinos have the right to change their responsible gaming programs to exclude me from their casinos even if I sign up only for the PlayUp Sportsbook self-exclusion list.',
		'I understand that the Ohio Gaming Commission is not responsible for keeping me informed of any changes',
		'By initiating this online exclusion, I agree not to gamble at PlayUp Sportsbook and understand will no longer be able to preform any actions on the site, nor receive any marketing emails or promotion, understand current pending wagers will stand as placed and any winnings can be withdrawn by contacting customer support',
	],
}[appConfig.regions.usState];

const PlayupExclusionForm = () => {
	const [selected, setSelected] = React.useState<Array<number>>([]);
	const [period, setPeriod] = React.useState<string | null>(null);
	const [showPrompt, setShowPrompt] = React.useState(false);

	const [loading, errorMessage, performAsyncAction] = useAsyncAction();

	const notify = useNotify();

	const handleSubmit = () => {
		performAsyncAction(async () => {
			if (!period) return;

			if (period.match(/lifetime/i)) {
				await selfExclude();
			} else {
				await takeBreak(period);
			}

			notify({
				type: 'success',
				title: 'Success',
				message: 'Your Break request was accepted.',
				timeout: 5000,
			});
		});
	};

	const canSubmit = selected.length === checkboxes?.length && !!period;

	if (!checkboxes) return null;

	const onCheck = (index: number) => () =>
		setSelected((oldSelected) => {
			if (oldSelected.includes(index)) return oldSelected.filter((sel) => sel !== index);
			return [...oldSelected, index];
		});

	return (
		<Wrapper>
			{showPrompt && (
				<Prompt
					onClose={() => setShowPrompt(false)}
					title="Confirm Self exclusion"
					message="Please note that this is a firm commitment."
					confirm="Proceed"
					onConfirm={handleSubmit}
				/>
			)}
			{checkboxes.map((label, i) => (
				<CheckBox key={label} checked={selected.includes(i)} onClick={onCheck(i)} label={label} />
			))}

			<SelectInput
				kind="white"
				value={period || ''}
				onChange={(e) => setPeriod(e.target.value)}
				error={parseApiError(errorMessage?.errors, 'period')}
			>
				<option value="">Select Period</option>
				{closePeriodsUS.map((close) => (
					<option key={close.period} value={close.period}>
						{close.period}
					</option>
				))}
			</SelectInput>

			{errorMessage?.errors && (
				<div className="_mb-3 _text-center form-error">{parseApiError(errorMessage.errors)}</div>
			)}
			<button disabled={!canSubmit || loading} onClick={() => setShowPrompt(true)}>
				Self-Exclude from Playup
			</button>
		</Wrapper>
	);
};

export default PlayupExclusionForm;

const Wrapper = styled.div`
	border-top: 1px solid grey;
	padding-top: 1rem;

	label {
		margin-bottom: 1rem;
		align-items: flex-start;
		cursor: pointer;
	}

	> button {
		background-color: ${({ theme }) => theme.colors.primary};
		width: 100%;
		padding: 1rem;
		border-radius: 0.25rem;
		margin-top: 1rem;
		font-weight: 700;
		transition: 400ms;

		&:disabled {
			background-color: ${({ theme }) => theme.colors.light};
			opacity: 0.6;
			cursor: not-allowed;
		}
	}
`;
